import React, { useMemo, useState } from 'react';
import {
  statusMeta,
  LOSS_REASON_LABEL,
  formatShortActivityDate,
  vendorTripNodeClass,
} from './workflowConstants';
import { VendorTripGlyph } from './WorkflowGlyphs';
import { Button } from '../ui/Button';
import {
  SECTION_CARD_HEADER,
  SECTION_CARD_HEADER_MAIN,
  SECTION_CARD_ICON,
  SECTION_CARD_TITLE_COL,
  SECTION_CARD_TITLE,
  SECTION_CARD_ACTIONS,
  SECTION_CARD_BODY_LEAD,
} from '../ui/DetailSection';

/**
 * Horizontal chain matching main Workflow strip — one node per vendor trip.
 * Send / receive actions stay on the order action bar (Choose next stage / primary actions).
 */
export function PostProductionTrips({ trips = [] }) {
  const [detailsOpen, setDetailsOpen] = useState(false);

  const anyLoss = useMemo(() => trips.some((t) => Number(t.piecesLost) > 0), [trips]);

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
      <header className={SECTION_CARD_HEADER}>
        <div className={SECTION_CARD_HEADER_MAIN}>
          <span className={SECTION_CARD_ICON}>
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
              />
            </svg>
          </span>
          <div className={SECTION_CARD_TITLE_COL}>
            <h3 className={SECTION_CARD_TITLE}>Vendor trips</h3>
          </div>
        </div>
        {trips.length > 0 ? (
          <div className={SECTION_CARD_ACTIONS}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-lg"
              onClick={() => setDetailsOpen((v) => !v)}
              aria-expanded={detailsOpen}
              title="Dates, quantities, and loss per trip"
            >
              Vendor Details
            </Button>
          </div>
        ) : null}
      </header>

      <div className="p-4 sm:p-5">
        {trips.length === 0 ? (
          <p className="rounded-lg bg-slate-50 px-2.5 py-2 text-xs text-slate-600">
            No trips yet. Use <span className="font-semibold">Choose next stage</span> to send; open{' '}
            <span className="font-semibold">Vendor Details</span> after trips appear to see dates and qty.
          </p>
        ) : (
          <>
            <VendorTripsChain trips={trips} />
            {detailsOpen ? (
              <div className="mt-2 border-t border-slate-100 pt-2">
                <TripLogTable trips={trips} anyLoss={anyLoss} />
              </div>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}

/** Compact strip: intrinsic width only — no flex-1, so 2–3 trips don’t stretch across the card. */
function VendorTripsChain({ trips }) {
  return (
    <div className="overflow-x-auto pb-0.5 text-center sm:text-left">
      <ol className="inline-flex w-max items-center gap-0 px-0.5">
        {trips.map((t, i) => {
          const meta = statusMeta(t.stage);
          const circle = vendorTripNodeClass(t.stage);
          const done = !t.active;
          const active = t.active;
          const isLast = i === trips.length - 1;
          const connectorDone = done && !isLast;
          const dateStr = done
            ? formatShortActivityDate(t.receivedAt || t.sentAt) || '—'
            : formatShortActivityDate(t.sentAt) || '—';

          return (
            <li key={`${t.tripNo}-${t.stage}-${t.sentAt}`} className="flex shrink-0 items-center">
              <div
                className="flex w-[76px] shrink-0 flex-col items-center sm:w-[84px]"
                aria-label={`Trip ${t.tripNo}, ${meta.label}, ${dateStr}`}
              >
                <span
                  className={[
                    'mb-1 line-clamp-2 max-h-[2.5em] w-full text-center text-[10px] font-bold leading-snug',
                    active ? 'text-amber-800' : done ? 'text-slate-800' : 'text-slate-400',
                  ].join(' ')}
                  title={meta.label}
                >
                  {meta.label}
                </span>
                <div className="relative shrink-0">
                  <div
                    className={[
                      'flex h-9 w-9 items-center justify-center rounded-full shadow-sm transition-all sm:h-10 sm:w-10',
                      done || active ? `${circle} text-white` : 'bg-slate-200 text-slate-500',
                      active ? 'ring-2 ring-amber-400/60' : '',
                    ].join(' ')}
                  >
                    <VendorTripGlyph stage={t.stage} className="h-4 w-4 sm:h-[18px] sm:w-[18px]" />
                  </div>
                  {done ? (
                    <span
                      className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 ring-1 ring-white"
                      aria-hidden
                    >
                      <svg className="h-2.5 w-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                  ) : null}
                </div>
                <p
                  className={[
                    'mt-1 w-full truncate text-center text-[9px] font-medium tabular-nums sm:text-[10px]',
                    active ? 'text-amber-900/85' : 'text-slate-500',
                  ].join(' ')}
                  title={dateStr}
                >
                  {dateStr}
                </p>
              </div>
              {!isLast ? (
                <div
                  className={[
                    'mx-0.5 h-1 w-3 shrink-0 self-center rounded-full sm:w-4',
                    connectorDone ? 'bg-emerald-500' : active ? 'bg-amber-300' : 'bg-slate-200',
                  ].join(' ')}
                  aria-hidden
                />
              ) : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function TripLogTable({ trips, anyLoss }) {
  if (!trips.length) return null;
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200">
      <table className="w-full min-w-[300px] border-collapse text-left text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50 text-[10px] font-bold uppercase tracking-wide text-slate-500">
            <th className="px-3 py-2">#</th>
            <th className="px-3 py-2">Work</th>
            <th className="px-3 py-2">Vendor</th>
            <th className="px-3 py-2">Out → In</th>
            <th className="px-3 py-2">Qty</th>
            {anyLoss ? <th className="px-3 py-2">Loss</th> : null}
          </tr>
        </thead>
        <tbody>
          {trips.map((t) => {
            const meta = statusMeta(t.stage);
            const lost = t.piecesLost != null ? Number(t.piecesLost) : 0;
            const dateOut = formatShortActivityDate(t.sentAt) || '—';
            const dateIn = t.receivedAt ? formatShortActivityDate(t.receivedAt) : '—';
            const qty = t.piecesSent != null ? `${t.piecesSent} sent` : '—';
            return (
              <tr key={`${t.tripNo}-${t.stage}-${t.sentAt}`} className="border-b border-slate-100 last:border-0">
                <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-slate-500">{t.tripNo}</td>
                <td className="px-3 py-2 font-semibold text-slate-800">
                  <span className="inline-flex flex-wrap items-center gap-1.5">
                    {meta.label}
                    {t.active ? (
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-900">Open</span>
                    ) : null}
                  </span>
                </td>
                <td className="max-w-[120px] truncate px-3 py-2 text-slate-600 sm:max-w-[200px]" title={t.vendorName || ''}>
                  {t.vendorName || '—'}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-600">
                  <span className="tabular-nums">{dateOut}</span>
                  <span className="mx-0.5 text-slate-300">→</span>
                  <span className="tabular-nums">{t.active ? <span className="font-medium text-amber-800">…</span> : dateIn}</span>
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-600">{qty}</td>
                {anyLoss ? (
                  <td className="px-3 py-2 text-xs text-slate-600">
                    {lost > 0 ? (
                      <>
                        {lost}
                        {t.lossReason ? (
                          <span className="text-slate-400"> ({LOSS_REASON_LABEL[t.lossReason] || t.lossReason})</span>
                        ) : null}
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
